import {
  ActionIcon,
  Badge,
  Modal,
  Text,
  Tooltip,
} from "@mantine/core";
import UniversalTable from "./Common/UniversalTable";
import React, { useEffect, useState } from "react";
import { getauditHistory } from "../apis/obligationApi";
import { formatDatetoISO, getComplianceBadgeColor } from "../pages/utilities";
import { IconFile, IconDownload, IconRobot } from "@tabler/icons-react";
import { getFilesForTran, getReadUrl } from "../apis/fileApi";
import EvidenceVerificationResult, {
  verificationStatusColor,
} from "./EvidenceVerificationResult";

// Shared by the live "Verify Evidence" flow (ViewObligation.jsx) and this
// audit history table's past-results view, both of which work with the same
// result shape returned by AI-Server's generic verification endpoint.
const ActionHistory = ({ id, openFile, refresh }) => {
  const [configs, setConfigs] = useState({
    loading: true,
    history: [],
  });
  const [downloading, setDownloading] = useState(false);
  const [opening, setOpening] = useState(false);
  const [aiModal, setAiModal] = useState(false);
  const [aiResults, setAiResults] = useState([]);

  const openFilesForTran = async (tranId, setLoading) => {
    setLoading(true);
    try {
      const response = await getFilesForTran({ id: tranId });
      if (response.success && response.files.length > 0) {
        for (const historyFile of response.files) {
          const fileResponse = await getReadUrl({ hostedLink: historyFile.hostedLink });
          if (fileResponse.success) {
            window.open(fileResponse.signedUrl, "_blank");
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getHistory = async () => {
      const response = await getauditHistory({ id });
      setConfigs({ loading: false, history: response.auditHistory });
    };
    getHistory();
  }, [id, refresh]);

  return (
    <>
      <div className="w-full">
        <UniversalTable
          headers={[
            {
              label: "Sequence Number",
              align: "center",
              render: (_, index) => <Text size="sm">{index + 1}</Text>,
            },
            {
              label: "Compliance Status",
              align: "center",
              render: (row) => (
                <Badge color={getComplianceBadgeColor(row.status)} variant="light">
                  {row.status}
                </Badge>
              ),
            },
            {
              label: "Actioned By",
              align: "center",
              render: (row) => <Text size="sm">{row.doneby}</Text>,
            },
            {
              label: "Actioned At",
              align: "center",
              render: (row) => <Text size="sm">{formatDatetoISO(row.attime)}</Text>,
            },
            {
              label: "Remarks",
              align: "center",
              render: (row) =>
                row.extraCol1 ? (
                  <Tooltip
                    label={row.extraCol1}
                    multiline
                    width={280}
                    withArrow
                    withinPortal
                    styles={{ tooltip: { whiteSpace: "normal", wordBreak: "break-word" } }}
                  >
                    <Text
                      size="sm"
                      className="mx-auto"
                      style={{
                        maxWidth: 240,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.extraCol1}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text size="sm">--</Text>
                ),
            },
            {
              label: "AI Status",
              align: "center",
              render: (row) =>
                row.aiStatus ? (
                  <Badge color={verificationStatusColor(row.aiStatus)}>{row.aiStatus}</Badge>
                ) : (
                  <Text size="sm" color="dimmed">--</Text>
                ),
            },
            {
              label: "Files",
              align: "center",
              render: (row) =>
                !row.fileIds ? (
                  <Text size="sm" color="dimmed">Evidence not uploaded</Text>
                ) : (
                  <div className="flex justify-center gap-2">
                    <ActionIcon
                      onClick={() => openFilesForTran(row.id, setDownloading)}
                      loading={downloading}
                    >
                      <IconDownload color="#1b6f8d" size={20} />
                    </ActionIcon>
                    <ActionIcon
                      onClick={() => openFilesForTran(row.id, setOpening)}
                      loading={opening}
                    >
                      <IconFile color="#1b6f8d" size={20} />
                    </ActionIcon>
                    {row.aiResults?.length > 0 && (
                      <ActionIcon
                        onClick={() => {
                          setAiResults(row.aiResults);
                          setAiModal(true);
                        }}
                      >
                        <IconRobot color="#1b6f8d" size={20} />
                      </ActionIcon>
                    )}
                  </div>
                ),
            },
          ]}
          data={configs.history}
          loading={configs.loading}
          emptyMessage="No history found"
        />
      </div>
      <Modal
        size="calc(60vw)"
        opened={aiModal}
        onClose={() => setAiModal(false)}
        title="AI evidence verification results"
      >
        {aiResults.map((entry) => (
          <div key={entry.fileId} className="mb-4">
            <Text className="font-semibold">{entry.fileName}</Text>
            <EvidenceVerificationResult result={entry.result} />
          </div>
        ))}
      </Modal>
    </>
  );
};

export default ActionHistory;
